import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import CloseIcon from "@mui/icons-material/Close";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import MemoryOutlinedIcon from "@mui/icons-material/MemoryOutlined";
import WcOutlinedIcon from "@mui/icons-material/WcOutlined";
import {
  Alert,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  getUserImportStatus,
  petHasMissingValues,
  userHasMissingValues,
} from "../utils/importUsersHelpers";
import EditPetDialog from "./EditPetDialog";
import EditUserDialog from "./EditUserDialog";
import ImportStatusChip from "./ImportStatusChip";
import { HOT_PINK } from "../../../../enums/Colors";

const MISSING_VALUE = "—";

// Which conflict (from useImportConflicts) maps to which message
const CONFLICT_MESSAGES = [
  ["emailInUse", "ImportEmailInUse"],
  ["emailDuplicate", "ImportEmailDuplicate"],
  ["phoneInUse", "ImportPhoneInUse"],
  ["phoneDuplicate", "ImportPhoneDuplicate"],
];

export default function UserDetailsDialog({ user, conflict, open, onClose, onUpdateUser }) {
  const { t } = useTranslation();

  function handlePetSave(index, updatedPet) {
    if (!user) return;
    const nextPets = user.pets.map((pet, i) => (i === index ? updatedPet : pet));
    onUpdateUser({ ...user, pets: nextPets });
  }

  const status = user ? getUserImportStatus(user, conflict) : null;
  const anyMissingValues =
    Boolean(user && userHasMissingValues(user)) ||
    (user?.pets?.some(petHasMissingValues) ?? false);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {user && (
        <>
          <DialogTitle sx={{ pb: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={2} alignItems="center">
                <Stack spacing={0.5}>
                  <Typography variant="h6" lineHeight={1.2}>
                    {user.name}
                  </Typography>
                  <ImportStatusChip status={status} />
                </Stack>
              </Stack>

              <Stack direction="row" spacing={0.5} alignItems="center">
                <EditUserDialog user={user} onSave={onUpdateUser} />
                <IconButton onClick={onClose} size="small" title={t("Close")}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </DialogTitle>

          <DialogContent dividers>
            <Stack spacing={2.5}>
              {conflict && (
                <Alert severity="error" variant="outlined" data-testid="import.user-conflict">
                  {CONFLICT_MESSAGES.filter(([field]) => conflict[field]).map(([field, key]) => (
                    <div key={field}>{t(key)}</div>
                  ))}
                </Alert>
              )}

              {anyMissingValues && (
                <Alert severity="warning" variant="outlined">
                  {t("MissingValuesUserImportMsg")}
                </Alert>
              )}

              <UserInfo user={user} />

              <Divider />

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle1" fontWeight={600}>
                  {t("PetsPlural")}
                </Typography>
              </Stack>

              <Stack spacing={1.5}>
                {user.pets.map((pet, index) => (
                  <PetCard key={index} pet={pet} index={index} onSave={handlePetSave} />
                ))}
              </Stack>
            </Stack>
          </DialogContent>
        </>
      )}
    </Dialog>
  );
}

function UserInfo({ user }) {
  const { t } = useTranslation();
  return (
    <Stack spacing={1.25}>
      <InfoRow icon={<BadgeOutlinedIcon fontSize="small" />} label={t("OIB")} value={user.oib} />
      <InfoRow icon={<HomeOutlinedIcon fontSize="small" />} label={t("Address")} value={user.address} />
      <InfoRow icon={<EmailOutlinedIcon fontSize="small" />} label={t("Email")} value={user.email} />
    </Stack>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Stack sx={{ color: "text.secondary" }}>{icon}</Stack>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 72 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={value ? 500 : 400}
        color={value ? "text.primary" : "text.disabled"}
      >
        {value || MISSING_VALUE}
      </Typography>
    </Stack>
  );
}

function PetCard({ pet, index, onSave }) {
  const { t } = useTranslation();
  const missing = petHasMissingValues(pet);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.75,
        borderRadius: 2,
        borderColor: missing ? "warning.light" : "divider",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Stack spacing={0.75} flex={1} minWidth={0}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle2" noWrap>
              {pet.name}
            </Typography>
            {pet.species && (
              <Chip
                size="small"
                color="info"
                label={pet.species}
                // Female pets get the same pink the pet profile uses; male keeps the default blue
                sx={
                  pet.gender === "FEMALE"
                    ? { bgcolor: HOT_PINK, color: "common.white" }
                    : undefined
                }
              />
            )}
            {pet.breed && (
              <Typography variant="caption" color="text.secondary">
                {pet.breed}
              </Typography>
            )}
          </Stack>

          <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={0.5}>
            <MiniDetail
              icon={<WcOutlinedIcon sx={{ fontSize: 14 }} />}
              value={pet.gender ? t(pet.gender) : null}
              fallback={MISSING_VALUE}
              label={t("Gender")}
            />
            {pet.birthDate && (
              <MiniDetail
                icon={<CakeOutlinedIcon sx={{ fontSize: 14 }} />}
                value={pet.birthDate}
                label={t("DateOfBirth")}
              />
            )}
            {pet.microchip && (
              <MiniDetail
                icon={<MemoryOutlinedIcon sx={{ fontSize: 14 }} />}
                value={pet.microchip}
                label={t("Microchip")}
              />
            )}
          </Stack>

          {(pet.updated || missing) && (
            <Stack direction="row" spacing={0.75}>
              {pet.updated && <ImportStatusChip status="updated" />}
              {missing && <ImportStatusChip status="missing" />}
            </Stack>
          )}
        </Stack>

        <EditPetDialog
          pet={pet}
          index={index}
          onSave={(updatedPet) => onSave(index, updatedPet)}
        />
      </Stack>
    </Paper>
  );
}

function MiniDetail({ icon, label, value, fallback }) {
  if (!value && !fallback) return null;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {icon}
      <Typography variant="caption" color="text.secondary">
        {label}: {value || fallback}
      </Typography>
    </Stack>
  );
}
